/**
 * ProjectModal — create or rename a project. Reuses Dashboard's existing
 * modal styling (.delete-confirm-overlay / .delete-confirm-modal) for
 * visual consistency.
 */

import React, { useEffect, useState } from 'react';

const COLORS = [
  '#42C3D7', '#FED402', '#F04E47', '#A78BFA',
  '#34D399', '#FB923C', '#F472B6', '#94A3B8',
];

const EMOJIS = ['📁', '🎨', '🎬', '✨', '🚀', '💼', '🧪', '🎯', '🌱', '🔥'];

export interface ProjectModalInput {
  name: string;
  color?: string | null;
  emoji?: string | null;
}

export interface ProjectModalProps {
  open: boolean;
  mode: 'create' | 'rename';
  initial?: ProjectModalInput;
  onClose: () => void;
  onSubmit: (input: ProjectModalInput) => void;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState<string | null>(initial?.color ?? COLORS[0]);
  const [emoji, setEmoji] = useState<string | null>(initial?.emoji ?? null);

  // Reset every time the modal opens with new initial state.
  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setColor(initial?.color ?? COLORS[0]);
      setEmoji(initial?.emoji ?? null);
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit({ name: trimmed, color, emoji });
  };

  return (
    <div className="delete-confirm-overlay" onClick={onClose}>
      <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h3>{mode === 'create' ? 'New project' : 'Rename project'}</h3>

        <input
          type="text"
          className="rename-input"
          placeholder="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          autoFocus
        />

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 8, letterSpacing: '0.05em' }}>
            COLOR
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: c,
                  border: color === c ? '2px solid #fff' : '2px solid transparent',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 8, letterSpacing: '0.05em' }}>
            EMOJI (optional)
          </label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setEmoji(null)}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                background: emoji === null ? '#42C3D7' : '#2a2a2a',
                color: emoji === null ? '#000' : '#aaa',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              None
            </button>
            {EMOJIS.map((em) => (
              <button
                key={em}
                type="button"
                onClick={() => setEmoji(em)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: emoji === em ? '#42C3D7' : '#2a2a2a',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 18,
                  padding: 0,
                }}
              >
                {em}
              </button>
            ))}
          </div>
        </div>

        <div className="delete-confirm-actions" style={{ marginTop: 24 }}>
          <button className="delete-confirm-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="delete-confirm-delete rename-confirm"
            onClick={handleSubmit}
            disabled={!name.trim()}
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
